/**
 * /lr/<location-slug>/<link-slug> — standalone informational page for
 * a single useful-link resource attached to a location.
 *
 *   Example: /lr/united-states/oregon/travel-oregon-tourism-board
 *
 * The last path segment is the slugified link caption. Everything
 * before it is the parent location slug (may be 1–3 segments).
 *
 * The page:
 *   • Fetches the parent location doc to resolve the actual
 *     `{url, caption, kind}` link entry (by slug-matching caption).
 *   • Generates an AI article about that resource via useAiTopic
 *     (source="link_reference"), so authors don't have to write
 *     boilerplate for every state tourism board / official portal.
 *   • Renders cross-nav pills to every *other* useful link for the
 *     same location, plus a breadcrumb back to the parent article.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { http } from "@/lib/api";
import { ArrowLeft, ExternalLink, Globe, Loader2 } from "lucide-react";
import AiTopicContent from "@/components/AiTopicContent";
import useAiTopic from "@/hooks/useAiTopic";
import RelatedLinksPanel from "@/components/RelatedLinksPanel";

const LINK_KIND_COLOR = {
  wikipedia:  "#3366cc",
  gov:        "#166534",
  tourism:    "#DB2777",
  wikivoyage: "#7C3AED",
  map:        "#0891B2",
  business:   "#B25D38",
};

// Mirrors slugifyCaption below — kept identical so URLs generated by
// LocationArticle round-trip through this page.
export function slugifyLinkCaption(caption) {
  return String(caption || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 120) || "link";
}

export default function LinkReferencePage() {
  const routeLoc = useLocation();
  const { locationSlug, linkSlug } = useMemo(() => {
    // URL is `/lr/<...location segments>/<link-slug>`
    const parts = routeLoc.pathname.replace(/^\/lr\/?/, "").split("/").filter(Boolean);
    if (parts.length < 2) return { locationSlug: "", linkSlug: "" };
    const linkSlug = parts[parts.length - 1];
    const locationSlug = parts.slice(0, -1).join("/");
    return { locationSlug, linkSlug };
  }, [routeLoc.pathname]);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    http.get(`/locations/${locationSlug}`)
      .then((r) => alive && setDoc(r.data))
      .catch((e) => alive && setErr(e?.response?.data?.detail || "Not found"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [locationSlug]);

  const link = useMemo(() => {
    if (!doc?.links?.length) return null;
    return doc.links.find((l) => slugifyLinkCaption(l.caption) === linkSlug) || null;
  }, [doc, linkSlug]);

  const otherLinks = useMemo(() => {
    if (!doc?.links) return [];
    return doc.links.filter((l) => slugifyLinkCaption(l.caption) !== linkSlug);
  }, [doc, linkSlug]);

  // AI content pipeline — pipes the location + link caption into the
  // topics engine so we get a themed article every time (e.g. what
  // Travel Oregon does, its history, what visitors can find on it).
  const topic = useAiTopic({
    source:    "link_reference",
    entityKey: link ? `${locationSlug}|${linkSlug}` : "",
    title:     link ? `${link.caption} — ${doc?.name || ""}`.trim() : "",
    context:   link
      ? `Write a friendly, evergreen encyclopedia entry about "${link.caption}" — a ${link.kind || "reference"} resource for ${doc?.name || locationSlug}. Cover: what the resource is, what visitors can find or do there, its role in the community, useful sections or tools it offers, and how it relates to nearby places. Keep the tone welcoming and factual. Do NOT paste URLs — they're already displayed on the page.`
      : "",
    deepLink:  `/lr/${locationSlug}/${linkSlug}`,
    enabled:   !!link,
  });

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-16 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" />
      </div>
    );
  }

  if (err || !doc) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center">
        <p className="opacity-70">Couldn't load this location.</p>
        <Link to="/" className="text-sm underline mt-4 inline-block">← Home</Link>
      </div>
    );
  }

  if (!link) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center" data-testid="link-ref-missing">
        <p className="opacity-70">This resource is no longer listed for {doc.name}.</p>
        <Link to={`/l/${doc.slug}`} className="text-sm underline mt-4 inline-block">
          ← Back to {doc.name}
        </Link>
      </div>
    );
  }

  const color = LINK_KIND_COLOR[link.kind] || "#334155";

  return (
    <article className="max-w-5xl mx-auto px-4 lg:px-6 py-8" data-testid="link-reference-article">
      {/* Breadcrumb */}
      <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-3 flex items-center gap-1 flex-wrap">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="opacity-40">·</span>
        <Link to={`/l/${doc.slug}`} className="hover:underline" data-testid="link-ref-crumb-location">
          {doc.name}
        </Link>
        <span className="opacity-40">·</span>
        <span>{link.caption}</span>
      </div>

      {/* Hero card — title + external CTA + kind chip */}
      <div
        className="rounded-2xl border-2 p-6 mb-6"
        style={{ borderColor: `${color}55`, background: `${color}0d` }}
        data-testid="link-ref-hero"
      >
        <div
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest rounded-full px-2 py-0.5 mb-3"
          style={{ background: `${color}22`, color }}
        >
          <Globe className="w-3 h-3" /> {link.kind || "reference"} · resource
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight" style={{ color }} data-testid="link-ref-title">
          {link.caption}
        </h1>
        <div className="text-sm opacity-80 mt-2">
          A useful resource for{" "}
          <Link to={`/l/${doc.slug}`} className="underline decoration-2 underline-offset-4">
            {doc.name}
          </Link>
        </div>
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white shadow-sm hover:shadow-md transition"
          style={{ background: color }}
          data-testid="link-ref-external-cta"
        >
          Visit {new URL(link.url.startsWith("http") ? link.url : `https://${link.url}`).hostname.replace(/^www\./, "")}
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <div className="grid lg:grid-cols-[1fr_260px] gap-6">
        {/* AI article */}
        <div className="min-w-0" data-testid="link-ref-body">
          <AiTopicContent
            doc={topic.doc}
            status={topic.status}
            retry={topic.retry}
            title={`${link.caption} — ${doc.name}`}
          />
          {/* Related web links — ~25 aggregated results (Wikipedia +
              DDG + search chips) with per-link bookmark toggle. */}
          <RelatedLinksPanel query={link.caption} />
        </div>

        {/* Right rail — other useful links + back to parent */}
        <aside className="lg:sticky lg:top-6 self-start space-y-4" data-testid="link-ref-nav">
          <div className="wofy-card">
            <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
              <Globe className="w-3 h-3 inline -mt-0.5 mr-1" /> MORE ABOUT {doc.name.toUpperCase()}
            </div>
            {otherLinks.length === 0 ? (
              <p className="text-xs opacity-60">No other useful links yet.</p>
            ) : (
              <ul className="space-y-2">
                {otherLinks.map((l) => {
                  const s = slugifyLinkCaption(l.caption);
                  return (
                    <li key={s}>
                      <Link
                        to={`/lr/${doc.slug}/${s}`}
                        className="text-sm hover:underline inline-flex items-start gap-1.5 group"
                        style={{ color: LINK_KIND_COLOR[l.kind] || "var(--wofy-text)" }}
                        data-testid={`link-ref-other-${s}`}
                      >
                        <Globe className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-60 group-hover:opacity-100" />
                        <span>{l.caption}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <Link
            to={`/l/${doc.slug}`}
            className="wofy-btn-secondary inline-flex items-center gap-2 w-full justify-center"
            data-testid="link-ref-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to {doc.name}
          </Link>
        </aside>
      </div>
    </article>
  );
}
